import React, { useState } from 'react';
import { useDashboardPresenter, Machine } from './presenter';
import { DashboardView } from './view';
import { machinesApi } from '../../../lib/apis';

export const DashboardPage: React.FC = () => {
  const { state } = useDashboardPresenter();
  const [itemsByMachine, setItemsByMachine] = useState<Record<string, any[]>>({});
  const [loadingByMachine, setLoadingByMachine] = useState<Record<string, boolean>>({});
  const [errorByMachine, setErrorByMachine] = useState<Record<string, string | null>>({});

  // Fetch products for each machine as soon as machines are loaded
  React.useEffect(() => {
    const fetchAll = async () => {
      const machines = state.machines || [];
      if (!machines.length) return;
      const newLoading: Record<string, boolean> = {};
      const newErrors: Record<string, string | null> = {};
      machines.forEach(m => { newLoading[m.id] = true; newErrors[m.id] = null; });
      setLoadingByMachine(prev => ({ ...prev, ...newLoading }));
      setErrorByMachine(prev => ({ ...prev, ...newErrors }));

      await Promise.all(machines.map(async (m) => {
        try {
          const res = await machinesApi.get<{ data: any[] }>(`/machines/${m.id}/products`);
          const list = (res as any)?.data ?? (res as any) ?? [];
          setItemsByMachine(prev => ({ ...prev, [m.id]: list }));
        } catch (e: any) {
          setErrorByMachine(prev => ({ ...prev, [m.id]: e.message || 'Failed to load products' }));
        } finally {
          setLoadingByMachine(prev => ({ ...prev, [m.id]: false }));
        }
      }));
    };
    fetchAll();
  }, [state.machines]);

  // Real-time stock updates via SSE
  React.useEffect(() => {
    // Prefer explicit notification base, then API base, then default to backend behind /admin/api on same origin
    const notifBase = (
      process.env.REACT_APP_NOTIFICATIONS_BASE ||
      process.env.REACT_APP_API_BASE ||
      (window.location && window.location.origin ? window.location.origin + '/admin/api' : 'http://localhost:3002')
    ).toString();
    const url = notifBase.replace(/\/$/, '') + '/notifications/stream';
    const es = new EventSource(url);

    const onStockUpdate = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data || '{}') as { machineId: string; updates: { productId: string; stock: number }[] };
        if (!data || !data.machineId || !Array.isArray(data.updates)) return;
        setItemsByMachine(prev => {
          const current = prev[data.machineId] || [];
          if (current.length === 0) return prev; // nothing loaded to patch
          const updatesMap = new Map<string, number>(data.updates.map(u => [String(u.productId), Number(u.stock)]));
          const patched = current.map(it => updatesMap.has(String(it.id)) ? { ...it, stock: updatesMap.get(String(it.id)) } : it);
          return { ...prev, [data.machineId]: patched };
        });
      } catch {}
    };

    es.addEventListener('stock_update', onStockUpdate as any);

    return () => {
      es.removeEventListener('stock_update', onStockUpdate as any);
      es.close();
    };
  }, []);


  const rowDetail = (m: Machine) => {
    const items = itemsByMachine[m.id] || [];
    const loading = !!loadingByMachine[m.id];
    const error = errorByMachine[m.id] || null;
    return (
      <div>
        <div className="mb-2 text-sm font-medium">Inventory — {m.name}</div>
        {error && <div className="mb-2 text-sm text-destructive">{error}</div>}
        {loading ? (
          <div>Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground">No products</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((it, i) => (
              <div key={it.id ?? it.sku ?? i} className="rounded-md border bg-card p-3 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-base font-semibold leading-tight">{it.name}</div>
                    <div className="text-xs text-muted-foreground">SKU: {it.sku || '-'}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">Stock</div>
                  <div className="rounded-md bg-muted px-2 py-1 text-sm font-medium">{it.stock ?? 0}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardView
      machines={state.machines}
      loading={state.loading}
      error={state.error}
      summaries={state.summaries}
      productCounts={state.productCounts}
      rowDetail={rowDetail}
    />
  );
};

export default DashboardPage;
